<template>
  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">Portfolio Allocation</v-card-title>
          <v-card-subtitle class="text-left"></v-card-subtitle>
        </v-card-item>
        <v-divider></v-divider>
        <v-skeleton-loader v-if="loading" type="table-tbody" height="400"></v-skeleton-loader>
        <v-card-text v-else>
          <v-table class="tag-table">
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-left">Broad</th>
                <th class="text-left">Value</th>
                <th class="text-left">Cost Base</th>
                <th class="text-right">% Portfolio</th>
                <th class="text-right">
                  <v-tooltip location="bottom" text="Annualised return on the current open position only. Resets when a position is fully sold and re-opened.">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props">IRR % (open)</span>
                    </template>
                  </v-tooltip>
                </th>
                <th class="text-right">
                  <v-tooltip location="bottom" text="Annualised return across all transactions ever made, including positions that were fully closed in the past.">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props">IRR % (all)</span>
                    </template>
                  </v-tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(category, catIndex) in security_allocations.broad_categories" :key="'broad-' + category.id">
                <!-- Render top-level tags and their descendants -->
                <template v-for="tag in flattened_security_allocations.filter(tag => tag.category.broad.id === category.id && tag.level === 0)" :key="tag.id">
                  <tr :class="{ 'clickable-row': tag.hasChildren }" @click="tag.hasChildren ? toggleTag(tag.id) : null">
                    <td :style="{ paddingLeft: `${tag.level * 25 + 16}px` }">
                      <v-icon v-if="tag.hasChildren" size="small" class="mr-2">
                        {{ expandedTags[tag.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                      {{ tag.name }}
                    </td>
                    <td>{{ tag.category.broad.name }}</td>
                    <td>{{ tag.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                    <td>{{ tag.cost_base != null ? tag.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                    <td class="text-right">{{ (tag.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                    <td class="text-right" :class="tag.irr?.open_position > 0 ? 'green_text' : tag.irr?.open_position < 0 ? 'red_text' : ''">{{ tag.irr?.open_position != null ? tag.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                    <td class="text-right" :class="tag.irr?.all_txns > 0 ? 'green_text' : tag.irr?.all_txns < 0 ? 'red_text' : ''">{{ tag.irr?.all_txns != null ? tag.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                  </tr>
                  <!-- Recursively render child tags -->
                  <template v-if="expandedTags[tag.id]">
                    <template v-for="child in renderChildTags(tag.id)" :key="child.id">
                      <tr :class="{ 'clickable-row': child.hasChildren }" @click="child.hasChildren ? toggleTag(child.id) : null">
                        <td :style="{ paddingLeft: `${child.level * 25 + 16}px` }">
                          <v-icon v-if="child.hasChildren" size="small" class="mr-2">
                            {{ expandedTags[child.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                          </v-icon>
                          {{ child.name }}
                        </td>
                        <td>{{ child.category.broad.name }}</td>
                        <td>{{ child.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                        <td>{{ child.cost_base != null ? child.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                        <td class="text-right">{{ (child.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                        <td class="text-right" :class="child.irr?.open_position > 0 ? 'green_text' : child.irr?.open_position < 0 ? 'red_text' : ''">{{ child.irr?.open_position != null ? child.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                        <td class="text-right" :class="child.irr?.all_txns > 0 ? 'green_text' : child.irr?.all_txns < 0 ? 'red_text' : ''">{{ child.irr?.all_txns != null ? child.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                      </tr>
                      <!-- Recursively render child's descendants -->
                      <template v-if="expandedTags[child.id]">
                        <template v-for="grandChild in renderChildTags(child.id)" :key="grandChild.id">
                          <tr :class="{ 'clickable-row': grandChild.hasChildren }" @click="grandChild.hasChildren ? toggleTag(grandChild.id) : null">
                            <td :style="{ paddingLeft: `${grandChild.level * 25 + 16}px` }">
                              <v-icon v-if="grandChild.hasChildren" size="small" class="mr-2">
                                {{ expandedTags[grandChild.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                              </v-icon>
                              {{ grandChild.name }}
                            </td>
                            <td>{{ grandChild.category.broad.name }}</td>
                            <td>{{ grandChild.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                            <td>{{ grandChild.cost_base != null ? grandChild.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                            <td class="text-right">{{ (grandChild.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                            <td class="text-right" :class="grandChild.irr?.open_position > 0 ? 'green_text' : grandChild.irr?.open_position < 0 ? 'red_text' : ''">{{ grandChild.irr?.open_position != null ? grandChild.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                            <td class="text-right" :class="grandChild.irr?.all_txns > 0 ? 'green_text' : grandChild.irr?.all_txns < 0 ? 'red_text' : ''">{{ grandChild.irr?.all_txns != null ? grandChild.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                          </tr>
                          <!-- Support deeper nesting if needed -->
                          <template v-if="expandedTags[grandChild.id]">
                            <template v-for="greatGrandChild in renderChildTags(grandChild.id)" :key="greatGrandChild.id">
                              <tr :class="{ 'clickable-row': greatGrandChild.hasChildren }" @click="greatGrandChild.hasChildren ? toggleTag(greatGrandChild.id) : null">
                                <td :style="{ paddingLeft: `${greatGrandChild.level * 25 + 16}px` }">
                                  <v-icon v-if="greatGrandChild.hasChildren" size="small" class="mr-2">
                                    {{ expandedTags[greatGrandChild.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                                  </v-icon>
                                  {{ greatGrandChild.name }}
                                </td>
                                <td>{{ greatGrandChild.category.broad.name }}</td>
                                <td>{{ greatGrandChild.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                                <td>{{ greatGrandChild.cost_base != null ? greatGrandChild.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                                <td class="text-right">{{ (greatGrandChild.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                                <td class="text-right" :class="greatGrandChild.irr?.open_position > 0 ? 'green_text' : greatGrandChild.irr?.open_position < 0 ? 'red_text' : ''">{{ greatGrandChild.irr?.open_position != null ? greatGrandChild.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                                <td class="text-right" :class="greatGrandChild.irr?.all_txns > 0 ? 'green_text' : greatGrandChild.irr?.all_txns < 0 ? 'red_text' : ''">{{ greatGrandChild.irr?.all_txns != null ? greatGrandChild.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                              </tr>
                              <!-- Ticker rows for greatGrandChild -->
                              <template v-if="expandedTags[greatGrandChild.id] && greatGrandChild.tickers && greatGrandChild.tickers.length > 0">
                                <tr v-for="tkr in greatGrandChild.tickers" :key="'tkr-' + greatGrandChild.id + '-' + tkr.ticker" class="ticker-row">
                                  <td :style="{ paddingLeft: `${(greatGrandChild.level + 1) * 25 + 16}px` }">
                                    <template v-if="tkr.broker_accounts && tkr.broker_accounts.length === 1">
                                      <router-link :to="`/brokeraccounts/${tkr.broker_accounts[0].id}/position/${tkr.ticker}`" class="ticker-link" target="_blank">{{ tkr.ticker }}</router-link>
                                    </template>
                                    <template v-else-if="tkr.broker_accounts && tkr.broker_accounts.length > 1">
                                      <span v-for="(ba, i) in tkr.broker_accounts" :key="ba.id">
                                        <router-link :to="`/brokeraccounts/${ba.id}/position/${tkr.ticker}`" class="ticker-link" :title="ba.name" target="_blank">{{ tkr.ticker }}</router-link><span v-if="i < tkr.broker_accounts.length - 1">, </span>
                                      </span>
                                    </template>
                                    <template v-else>{{ tkr.ticker }}</template>
                                  </td>
                                  <td>{{ greatGrandChild.category.broad.name }}</td>
                                  <td>{{ tkr.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                                  <td>{{ tkr.cost_base != null ? tkr.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                                  <td class="text-right">{{ (tkr.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                                  <td class="text-right" :class="tkr.irr?.open_position > 0 ? 'green_text' : tkr.irr?.open_position < 0 ? 'red_text' : ''">{{ tkr.irr?.open_position != null ? tkr.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                                  <td class="text-right" :class="tkr.irr?.all_txns > 0 ? 'green_text' : tkr.irr?.all_txns < 0 ? 'red_text' : ''">{{ tkr.irr?.all_txns != null ? tkr.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                                </tr>
                              </template>
                            </template>
                          </template>
                          <!-- Ticker rows for grandChild -->
                          <template v-if="expandedTags[grandChild.id] && grandChild.tickers && grandChild.tickers.length > 0">
                            <tr v-for="tkr in grandChild.tickers" :key="'tkr-' + grandChild.id + '-' + tkr.ticker" class="ticker-row">
                              <td :style="{ paddingLeft: `${(grandChild.level + 1) * 25 + 16}px` }">
                                <template v-if="tkr.broker_accounts && tkr.broker_accounts.length === 1">
                                  <router-link :to="`/brokeraccounts/${tkr.broker_accounts[0].id}/position/${tkr.ticker}`" class="ticker-link" target="_blank">{{ tkr.ticker }}</router-link>
                                </template>
                                <template v-else-if="tkr.broker_accounts && tkr.broker_accounts.length > 1">
                                  <span v-for="(ba, i) in tkr.broker_accounts" :key="ba.id">
                                    <router-link :to="`/brokeraccounts/${ba.id}/position/${tkr.ticker}`" class="ticker-link" :title="ba.name" target="_blank">{{ tkr.ticker }}</router-link><span v-if="i < tkr.broker_accounts.length - 1">, </span>
                                  </span>
                                </template>
                                <template v-else>{{ tkr.ticker }}</template>
                              </td>
                              <td>{{ grandChild.category.broad.name }}</td>
                              <td>{{ tkr.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                              <td>{{ tkr.cost_base != null ? tkr.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                              <td class="text-right">{{ (tkr.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                              <td class="text-right" :class="tkr.irr?.open_position > 0 ? 'green_text' : tkr.irr?.open_position < 0 ? 'red_text' : ''">{{ tkr.irr?.open_position != null ? tkr.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                              <td class="text-right" :class="tkr.irr?.all_txns > 0 ? 'green_text' : tkr.irr?.all_txns < 0 ? 'red_text' : ''">{{ tkr.irr?.all_txns != null ? tkr.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                            </tr>
                          </template>
                        </template>
                      </template>
                      <!-- Ticker rows for child -->
                      <template v-if="expandedTags[child.id] && child.tickers && child.tickers.length > 0">
                        <tr v-for="tkr in child.tickers" :key="'tkr-' + child.id + '-' + tkr.ticker" class="ticker-row">
                          <td :style="{ paddingLeft: `${(child.level + 1) * 25 + 16}px` }">
                            <template v-if="tkr.broker_accounts && tkr.broker_accounts.length === 1">
                              <router-link :to="`/brokeraccounts/${tkr.broker_accounts[0].id}/position/${tkr.ticker}`" class="ticker-link" target="_blank">{{ tkr.ticker }}</router-link>
                            </template>
                            <template v-else-if="tkr.broker_accounts && tkr.broker_accounts.length > 1">
                              <span v-for="(ba, i) in tkr.broker_accounts" :key="ba.id">
                                <router-link :to="`/brokeraccounts/${ba.id}/position/${tkr.ticker}`" class="ticker-link" :title="ba.name" target="_blank">{{ tkr.ticker }}</router-link><span v-if="i < tkr.broker_accounts.length - 1">, </span>
                              </span>
                            </template>
                            <template v-else>{{ tkr.ticker }}</template>
                          </td>
                          <td>{{ child.category.broad.name }}</td>
                          <td>{{ tkr.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                          <td>{{ tkr.cost_base != null ? tkr.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                          <td class="text-right">{{ (tkr.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                          <td class="text-right" :class="tkr.irr?.open_position > 0 ? 'green_text' : tkr.irr?.open_position < 0 ? 'red_text' : ''">{{ tkr.irr?.open_position != null ? tkr.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                          <td class="text-right" :class="tkr.irr?.all_txns > 0 ? 'green_text' : tkr.irr?.all_txns < 0 ? 'red_text' : ''">{{ tkr.irr?.all_txns != null ? tkr.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                        </tr>
                      </template>
                    </template>
                    <!-- Ticker rows for tag -->
                    <template v-if="tag.tickers && tag.tickers.length > 0">
                      <tr v-for="tkr in tag.tickers" :key="'tkr-' + tag.id + '-' + tkr.ticker" class="ticker-row">
                        <td :style="{ paddingLeft: `${(tag.level + 1) * 25 + 16}px` }">
                          <template v-if="tkr.broker_accounts && tkr.broker_accounts.length === 1">
                            <router-link :to="`/brokeraccounts/${tkr.broker_accounts[0].id}/position/${tkr.ticker}`" class="ticker-link" target="_blank">{{ tkr.ticker }}</router-link>
                          </template>
                          <template v-else-if="tkr.broker_accounts && tkr.broker_accounts.length > 1">
                            <span v-for="(ba, i) in tkr.broker_accounts" :key="ba.id">
                              <router-link :to="`/brokeraccounts/${ba.id}/position/${tkr.ticker}`" class="ticker-link" :title="ba.name" target="_blank">{{ tkr.ticker }}</router-link><span v-if="i < tkr.broker_accounts.length - 1">, </span>
                            </span>
                          </template>
                          <template v-else>{{ tkr.ticker }}</template>
                        </td>
                        <td>{{ tag.category.broad.name }}</td>
                        <td>{{ tkr.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</td>
                        <td>{{ tkr.cost_base != null ? tkr.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</td>
                        <td class="text-right">{{ (tkr.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</td>
                        <td class="text-right" :class="tkr.irr?.open_position > 0 ? 'green_text' : tkr.irr?.open_position < 0 ? 'red_text' : ''">{{ tkr.irr?.open_position != null ? tkr.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                        <td class="text-right" :class="tkr.irr?.all_txns > 0 ? 'green_text' : tkr.irr?.all_txns < 0 ? 'red_text' : ''">{{ tkr.irr?.all_txns != null ? tkr.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</td>
                      </tr>
                    </template>
                  </template>
                </template>
                <!-- Summary row for the broad category -->
                <tr class="summary-row">
                  <td :style="{ paddingLeft: '16px' }"></td>
                  <td></td>
                  <td><strong>{{ category.value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</strong></td>
                  <td><strong>{{ category.cost_base != null ? category.cost_base.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '--' }}</strong></td>
                  <td class="text-right"><strong>{{ (category.pct_portfolio * 100).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}%</strong></td>
                  <td class="text-right" :class="category.irr?.open_position > 0 ? 'green_text' : category.irr?.open_position < 0 ? 'red_text' : ''"><strong>{{ category.irr?.open_position != null ? category.irr.open_position.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</strong></td>
                  <td class="text-right" :class="category.irr?.all_txns > 0 ? 'green_text' : category.irr?.all_txns < 0 ? 'red_text' : ''"><strong>{{ category.irr?.all_txns != null ? category.irr.all_txns.toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '--' }}</strong></td>
                </tr>
                <!-- Blank row after each category -->
                <tr v-if="catIndex < security_allocations.broad_categories.length - 1" class="blank-row">
                  <td colspan="7"></td>
                </tr>
              </template>
              <!-- Portfolio total row at bottom -->
              <tr class="portfolio-total-row">
                <td></td>
                <td></td>
                <td><strong>{{ security_allocations.portfolio_value.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</strong></td>
                <td></td>
                <td class="text-right"><strong>100.00%</strong></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations} from 'vuex';
import CategoryNewModal from './modals/CategoryNewModalPopup.vue';
import CategoryEditModal from './modals/CategoryEditModalPopup.vue';
import CategoryAddTagModal from './modals/CategoryAddTagModalPopup.vue';

export default {
  components: {
    CategoryNewModal,
    CategoryEditModal,
    CategoryAddTagModal
  },
  data() {
    return {
      security_allocations: {
        portfolio_value: 0,
        broad_categories: [],
        tag_tree: []
      },
      loading: true,
      flattened_security_allocations: [],
      expandedTags: {} // Tracks which tags are expanded
    };
  },
  async created() {
    await this.getSecurityAllocations();
    // Initialize all tags with children as collapsed
    this.flattened_security_allocations.forEach(tag => {
      if (tag.hasChildren) {
        this.expandedTags[tag.id] = false;
      }
    });
  },
  computed: {
    ...mapState({
      token: "token"
    })
  },
  methods: {
      ...mapMutations(["showSnackbar"]),
    async getSecurityAllocations() {
      this.loading = true;
      try {
        const response = await axios.get("/reports/securityallocation/", {
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        });
        this.security_allocations = response.data;
        this.flattened_security_allocations = this.flattenTags(this.security_allocations.tag_tree)
          .sort((a, b) => a.category.broad.id - b.category.broad.id); // Sort by broad category ID
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load security allocations' });
      } finally {
        this.loading = false;
      }
    },
    flattenTags(tags, level = 0, parentName = '') {
      const result = [];
      
      tags.forEach(tag => {
        const flatTag = {
          id: tag.id,
          category: {
            broad: {
              id: tag.category.broad.id,
              name: tag.category.broad.name
            }
          },
          name: tag.name,
          value: tag.value,
          pct_portfolio: tag.pct_portfolio,
          irr: tag.irr || null,
          cost_base: tag.cost_base,
          tickers: tag.tickers || [],
          parent_tag_id: tag.parent_tag_id,
          level,
          parentName: parentName || 'None',
          hasChildren: (tag.children && tag.children.length > 0) || (tag.tickers && tag.tickers.length > 0)
        };
        result.push(flatTag);
        
        if (tag.children && tag.children.length > 0) {
          const childTags = this.flattenTags(tag.children, level + 1, tag.name);
          result.push(...childTags);
        }
      });
      return result;
    },
    toggleTag(tagId) {
      this.expandedTags[tagId] = !this.expandedTags[tagId];
    },
    renderChildTags(parentId) {
      return this.flattened_security_allocations.filter(tag => tag.parent_tag_id === parentId);
    }
  }
};
</script>

<style scoped>
.text-h4 {
  font-family: 'Courier New', Courier, monospace, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

table {
  width: 100%;
}

table tr.clickable-row {
  cursor: pointer;
}

.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

.green_text {
  color: green;
}

.red_text {
  color: red;
}

.tag-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.tag-table th,
.tag-table td {
  padding: 12px;
  text-align: left !important; /* Override Vuetify's default centering */
}

.tag-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.tag-table tr:nth-child(even) {
  background-color: #fafafa;
}

.tag-table tr:hover {
  background-color: #f0f0f0;
}

/* Sticky Name column */
.tag-table th:first-child,
.tag-table td:first-child {
  position: sticky;
  left: 0;
  min-width: 150px;
  box-shadow: 2px 0 4px -1px rgba(0, 0, 0, 0.12);
}

.tag-table th:first-child {
  z-index: 3;
  background-color: #f5f5f5;
}

.tag-table td:first-child {
  z-index: 2;
  background-color: #ffffff;
}

.tag-table tr:nth-child(even) td:first-child {
  background-color: #fafafa;
}

.tag-table tr:hover td:first-child {
  background-color: #f0f0f0;
}

@media (max-width: 599px) {
  .tag-table th,
  .tag-table td {
    padding: 8px;
  }
}

.summary-row {
  font-weight: bold;
}

.ticker-row {
  font-style: italic;
  color: #666;
}

.ticker-link {
  color: inherit;
  text-decoration: none;
}

.ticker-link:hover {
  text-decoration: underline;
}
</style>